import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle
} from '@/components/ui/card'
// Removed Select import - using native dropdown to avoid scroll lock
// Removed Separator import - not needed with native select
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table'
import { TournamentPodium } from '@/components/knockout/TournamentPodium'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { apiClient, handleApiError } from '@/services/api'
import { useAuthStore } from '@/store/auth'
import type { Series, Club, Podium, Player } from '@/types/api'
import { Icon, ICONS } from '@/lib/icons'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { exportLeaderboardToCSV, type LeaderboardCSVData } from '@/utils/csvExport'

interface SeriesByClub {
  [clubId: string]: {
    club: Club | null
    series: Series[]
  }
}

type UILBRow = {
  rank: number
  playerId: string
  displayName: string
  rating: number
  games: number
  wins: number
  losses: number
  draws: number
}

type PodiumEntryView = {
  players: Player[]
  matchesPlayed: number
  wins: number
  losses: number
  winRate?: number
  extraStats?: {
    setDifference?: number
    eloRating?: number
    ladderPosition?: number
  }
}

type PodiumViewModel = {
  champion: PodiumEntryView
  runnerUp: PodiumEntryView
  thirdPlace?: PodiumEntryView
}

// ---- helpers to tolerate API shape differences (rows vs entries, snake_case vs camelCase)
function normalizeLeaderboard(resp: { rows?: unknown[]; entries?: unknown[] } | unknown): UILBRow[] {
  const raw = ((resp as { rows?: unknown[]; entries?: unknown[] })?.rows ?? 
               (resp as { rows?: unknown[]; entries?: unknown[] })?.entries ?? []) as Array<Record<string, unknown>>
  if (!Array.isArray(raw)) return []
  return raw.map((r: Record<string, unknown>) => ({
    rank: Number(r.rank ?? 0),
    playerId: String(r.playerId ?? r.player_id ?? ''),
    displayName: String(r.displayName ?? r.playerName ?? r.display_name ?? ''),
    rating: Number(r.rating ?? r.eloRating ?? 0),
    games: Number(r.games ?? r.matchesPlayed ?? 0),
    wins: Number(r.wins ?? r.matchesWon ?? 0),
    losses: Number(r.losses ?? r.matchesLost ?? 0),
    draws: Number(r.draws ?? r.matchesDrawn ?? 0),
  }))
}

export function LeaderboardPage() {
  const { id: seriesIdFromParams } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const { selectedClubId, user } = useAuthStore()
  const [searchParams, setSearchParams] = useSearchParams()
  const querySeriesId = searchParams.get('series') || ''
  const initialSelectedSeriesId = seriesIdFromParams || querySeriesId || ''

  const [selectedSeriesId, setSelectedSeriesId] = useState<string>(initialSelectedSeriesId)
  const [seriesDropdownOpen, setSeriesDropdownOpen] = useState(false)
  const [leaderboard, setLeaderboard] = useState<UILBRow[]>([])
  const [seriesByClub, setSeriesByClub] = useState<SeriesByClub>({})
  const [openSeries, setOpenSeries] = useState<Series[]>([])
  const [_clubs, setClubs] = useState<{ [id: string]: Club }>({})
  const [loadingSeries, setLoadingSeries] = useState(true)
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false)
  const [selectedSeriesDetails, setSelectedSeriesDetails] = useState<Series | null>(null)
  const [podiumView, setPodiumView] = useState<PodiumViewModel | null>(null)
  const [loadingPodium, setLoadingPodium] = useState(false)
  const [podiumError, setPodiumError] = useState<string | null>(null)

  const updateSeriesSelection = useCallback(
    (nextId: string) => {
      setSelectedSeriesId(nextId)
      if (nextId) {
        setSearchParams({ series: nextId }, { replace: true })
      } else {
        setSearchParams({}, { replace: true })
      }
    },
    [setSearchParams],
  )

  const buildFallbackPlayer = useCallback(
    (id: string, fallbackName?: string): Player => ({
      id,
      displayName:
        fallbackName && fallbackName.trim().length > 0
          ? fallbackName
          : t('leaderboard.unknownPlayer', 'Unknown player'),
      active: true,
      email: '',
      firstName: '',
      lastName: '',
      clubMemberships: [],
      isPlatformOwner: false,
      createdAt: new Date(0).toISOString(),
      statsBySport: {},
    }),
    [t],
  )

  const loadSeries = useCallback(async () => {
    try {
      setLoadingSeries(true)
      
      // Build filter based on selected club (same logic as SeriesListPage)
      let clubFilter: string[] = []
      
      if (!selectedClubId || selectedClubId === 'all-clubs') {
        // "Alla klubbar" - send empty list to show all series
        clubFilter = []
      } else if (selectedClubId === 'my-clubs') {
        // "Mina klubbar" - resolve to actual club IDs + open series
        const userClubIds = user?.memberships?.map(m => m.clubId).filter(Boolean) || []
        clubFilter = [...userClubIds, 'OPEN']
      } else {
        // Specific club selected - send that club ID + open series
        clubFilter = [selectedClubId, 'OPEN']
      }
      
      const response = await apiClient.listSeries({
        pageSize: 100,
        clubFilter
      })
      
      const items: Series[] = Array.isArray(response?.items) ? response.items : []
      
      // Load clubs information for displaying club names
      const allClubIds = [...new Set(items.map(s => s.clubId).filter((id): id is string => Boolean(id)))]
      const clubsData: { [id: string]: Club } = {}
      
      await Promise.all(
        allClubIds.map(async (clubId) => {
          try {
            const club = await apiClient.getClub(clubId)
            clubsData[clubId] = club
          } catch (_error) {
            // Club might not exist or be accessible - silently skip
          }
        })
      )
      
      setClubs(clubsData)

      const completedItems = items.filter(seriesItem => seriesItem.state === 'SERIES_STATE_COMPLETED')

      // Separate series by club and open series (only completed)
      const seriesByClubData: SeriesByClub = {}
      const openSeriesData: Series[] = []

      completedItems.forEach(series => {
        if (series.visibility === 'SERIES_VISIBILITY_OPEN') {
          openSeriesData.push(series)
        } else if (series.clubId) {
          if (!seriesByClubData[series.clubId]) {
            seriesByClubData[series.clubId] = {
              club: clubsData[series.clubId] || null,
              series: []
            }
          }
          seriesByClubData[series.clubId].series.push(series)
        }
      })

      setSeriesByClub(seriesByClubData)
      setOpenSeries(openSeriesData)

      const completedIds = new Set(completedItems.map(seriesItem => seriesItem.id))
      
      // Use functional state update to read current selectedSeriesId without making it a dependency
      setSelectedSeriesId(currentSelectedId => {
        const priorityIds = [seriesIdFromParams, querySeriesId, currentSelectedId].filter(
          (value): value is string => Boolean(value),
        )

        const nextSelectedId = (() => {
          if (completedItems.length === 0) {
            return ''
          }

          const matched = priorityIds.find(id => completedIds.has(id))
          if (matched) {
            return matched
          }

          return completedItems[0].id
        })()

        // Don't update URL here to avoid "Cannot update component while rendering" error
        // URL will be synced via useEffect below
        return nextSelectedId !== currentSelectedId ? nextSelectedId : currentSelectedId
      })
    } catch (error: unknown) {
      toast.error((error as Error)?.message || t('error.generic'))
      setSeriesByClub({})
      setOpenSeries([])
    } finally {
      setLoadingSeries(false)
    }
  }, [selectedClubId, user, seriesIdFromParams, querySeriesId, t])

  // Sync URL with selectedSeriesId
  useEffect(() => {
    if (selectedSeriesId) {
      setSearchParams({ series: selectedSeriesId }, { replace: true })
    } else {
      setSearchParams({}, { replace: true })
    }
  }, [selectedSeriesId, setSearchParams])

  useEffect(() => {
    loadSeries()
  }, [loadSeries, selectedClubId])

  useEffect(() => {
    const nextId = querySeriesId || seriesIdFromParams || ''
    setSelectedSeriesId(prev => {
      if (nextId && prev !== nextId) {
        return nextId
      }
      if (!nextId && prev) {
        return ''
      }
      return prev
    })
  }, [seriesIdFromParams, querySeriesId])

  const loadLeaderboard = useCallback(async (seriesId: string) => {
    try {
      setLoadingLeaderboard(true)
      const resp = await apiClient.getLeaderboard({ seriesId, pageSize: 50 }, 'leaderboard')
      setLeaderboard(normalizeLeaderboard(resp))
    } catch (error: unknown) {
      handleApiError(error, (apiError) => {
        toast.error(apiError.message || t('error.generic'))
        setLeaderboard([])
      })
    } finally {
      setLoadingLeaderboard(false)
    }
  }, [t])

  useEffect(() => {
    if (selectedSeriesId) {
      loadLeaderboard(selectedSeriesId)
    } else {
      setLeaderboard([])
    }
  }, [selectedSeriesId, loadLeaderboard])

  useEffect(() => {
    if (!selectedSeriesId) {
      setSelectedSeriesDetails(null)
      return
    }

    let isMounted = true
    setSelectedSeriesDetails(null)
    apiClient
      .getSeries(selectedSeriesId)
      .then((series) => {
        if (isMounted) {
          setSelectedSeriesDetails(series)
        }
      })
      .catch((error: unknown) => {
        if (!isMounted) return
        handleApiError(error, (apiError) => {
          toast.error(apiError.message || t('error.generic'))
          setSelectedSeriesDetails(null)
        })
      })

    return () => {
      isMounted = false
    }
  }, [selectedSeriesId, t])

  const calculateWinRate = (wins: number, games: number) => {
    if (!games) return 0
    return Math.round((wins / games) * 100)
  }

  const handleExportCSV = () => {
    const csvData: LeaderboardCSVData[] = leaderboard.map((row) => ({
      rank: row.rank,
      player: row.displayName,
      rating: Math.round(row.rating),
      games: row.games,
      wins: row.wins,
      draws: row.draws,
      losses: row.losses,
      winRate: `${calculateWinRate(row.wins, row.games)}%`
    }))
    
    exportLeaderboardToCSV(csvData, selectedSeries?.title)
    toast.success(t('leaderboard.exportSuccess'))
  }

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Icon icon={ICONS.medal} width={20} height={20} className="text-yellow-500" /> // ALLOWED: Gold medal
    if (rank === 2) return <Icon icon={ICONS.medal} width={20} height={20} className="text-gray-400" /> // ALLOWED: Silver medal
    if (rank === 3) return <Icon icon={ICONS.medal} width={20} height={20} className="text-orange-600" /> // ALLOWED: Bronze medal
    return null
  }

  const getRankBadge = (rank: number) => {
    if (rank <= 3) return 'default' as const
    if (rank <= 10) return 'secondary' as const
    return 'outline' as const
  }

  // Get all series for finding selected series
  const allSeries = [
    ...Object.values(seriesByClub).flatMap(club => club.series),
    ...openSeries
  ]
  
  const selectedSeries = allSeries.find((s) => s.id === selectedSeriesId) || null
  const seriesToDisplay = selectedSeriesDetails ?? selectedSeries
  const isSeriesCompleted = seriesToDisplay?.state === 'SERIES_STATE_COMPLETED'
  const podiumToDisplay = seriesToDisplay?.podium
  const leaderboardTitle = isSeriesCompleted
    ? t('leaderboard.final_standings', 'Final Standings')
    : t('leaderboard.current_standings', 'Current Standings')

  useEffect(() => {
    if (!isSeriesCompleted || !podiumToDisplay) {
      setPodiumView(null)
      setPodiumError(null)
      setLoadingPodium(false)
      return
    }

    let isCancelled = false

    const loadPodiumPlayers = async () => {
      setLoadingPodium(true)
      setPodiumError(null)

      try {
        const uniquePlayerIds = new Set<string>()
        podiumToDisplay.placements.forEach(placement => {
          placement.playerIds.forEach(playerId => {
            if (playerId) {
              uniquePlayerIds.add(playerId)
            }
          })
        })

        const playersById = new Map<string, Player>()

        await Promise.all(
          Array.from(uniquePlayerIds).map(async playerId => {
            try {
              const player = await apiClient.getPlayer(playerId)
              if (!isCancelled) {
                playersById.set(playerId, player)
              }
            } catch (_error) {
              if (isCancelled) {
                return
              }
              const fallbackPlacement = podiumToDisplay.placements.find(placement =>
                placement.playerIds.includes(playerId),
              )
              playersById.set(playerId, buildFallbackPlayer(playerId, fallbackPlacement?.playerName))
            }
          }),
        )

        const resolvePlacement = (placement: Podium['placements'][number]): PodiumEntryView | null => {
          const players = placement.playerIds.length
            ? placement.playerIds.map((playerId, index) => {
                if (!playerId) {
                  return buildFallbackPlayer(`placement-${placement.placement}-${index}`, placement.playerName)
                }
                return playersById.get(playerId) ?? buildFallbackPlayer(playerId, placement.playerName)
              })
            : [buildFallbackPlayer(`placement-${placement.placement}`, placement.playerName)]

          const wins = placement.stats.wins ?? 0
          const losses = placement.stats.losses ?? 0
          const matchesPlayed = wins + losses
          const winRate = matchesPlayed > 0 ? Math.round((wins / matchesPlayed) * 100) : undefined

          return {
            players,
            matchesPlayed,
            wins,
            losses,
            winRate,
            extraStats: {
              setDifference: placement.stats.setDifference,
              eloRating: placement.stats.eloRating,
              ladderPosition: placement.stats.ladderPosition,
            },
          }
        }

        const championPlacement = podiumToDisplay.placements.find(placement => placement.placement === 1)
        const runnerUpPlacement = podiumToDisplay.placements.find(placement => placement.placement === 2)

        if (!championPlacement || !runnerUpPlacement) {
          throw new Error('missing_podium_placements')
        }

        const championEntry = resolvePlacement(championPlacement)
        const runnerUpEntry = resolvePlacement(runnerUpPlacement)
        const thirdPlacement = podiumToDisplay.placements.find(placement => placement.placement === 3)
        const thirdEntry = thirdPlacement ? resolvePlacement(thirdPlacement) ?? undefined : undefined

        if (!championEntry || !runnerUpEntry) {
          throw new Error('missing_podium_players')
        }

        if (!isCancelled) {
          setPodiumView({ champion: championEntry, runnerUp: runnerUpEntry, thirdPlace: thirdEntry || undefined })
        }
      } catch (_error) {
        if (!isCancelled) {
          setPodiumError(t('leaderboard.podiumErrorMessage', 'Unable to load podium data right now.'))
          setPodiumView(null)
        }
      } finally {
        if (!isCancelled) {
          setLoadingPodium(false)
        }
      }
    }

    loadPodiumPlayers()

    return () => {
      isCancelled = true
    }
  }, [isSeriesCompleted, podiumToDisplay, buildFallbackPlayer, t])

  if (loadingSeries) {
    return <LoadingSpinner />
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t('leaderboard.title')}</h1>
          <p className="text-muted-foreground">{t('leaderboard.subtitle', 'Player rankings and statistics')}</p>
        </div>
      </div>

      {/* Series Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 text-lg">
            <Icon icon={ICONS.tournament} width={18} height={18} className="text-success" />
            <span>{t('leaderboard.select.series')}</span>
          </CardTitle>
          <CardDescription>
            <span>{t('leaderboard.select.help')}</span>
            <br />
            <span>{t('series.completedSeriesOnly', 'Only completed series are displayed in this list.')}</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative max-w-md">
            {/* Trigger Button */}
            <button
              type="button"
              role="combobox"
              aria-expanded={seriesDropdownOpen}
              onClick={() => setSeriesDropdownOpen(!seriesDropdownOpen)}
              className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <div className="flex items-center space-x-2">
                <Icon icon={ICONS.tournament} width={16} height={16} className="text-success" />
                <span className="truncate">
                  {selectedSeries ? selectedSeries.title : t('leaderboard.select.series')}
                </span>
              </div>
              <svg
                className="ml-2 h-4 w-4 shrink-0 opacity-50"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6,9 12,15 18,9" />
              </svg>
            </button>

            {/* Native Dropdown - No Radix, No Body Scroll Lock */}
            {seriesDropdownOpen && (
              <>
                {/* Backdrop */}
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setSeriesDropdownOpen(false)}
                />
                
                {/* Dropdown Content */}
                <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-popover text-popover-foreground rounded-md border shadow-md">
                  <div className="max-h-60 overflow-y-auto overscroll-contain p-1" style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }}>
                    {/* Club Series Sections First */}
                    {Object.entries(seriesByClub).map(([clubId, clubData]) => [
                      // Club header
                      <div key={`header-${clubId}`} className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 rounded-sm">
                        {clubData.club?.name || t('series.sections.unknownClub')}
                      </div>,
                      // Club series
                      ...clubData.series.map((seriesItem) => (
                        <div
                          key={seriesItem.id}
                          onClick={() => {
                            updateSeriesSelection(seriesItem.id)
                            setSeriesDropdownOpen(false)
                          }}
                          className="flex items-center space-x-2 px-2 py-2 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground rounded-sm ml-4"
                          data-testid="series-option"
                          data-state={seriesItem.state || 'SERIES_STATE_UNSPECIFIED'}
                        >
                          <Icon icon={ICONS.tournament} width={16} height={16} className="text-success" />
                          <span className="flex-1">{seriesItem.title}</span>
                          {selectedSeriesId === seriesItem.id && (
                            <svg
                              className="ml-auto h-4 w-4 text-success"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20,6 9,17 4,12" />
                            </svg>
                          )}
                        </div>
                      ))
                    ]).flat()}
                    
                    {/* Separator if we have both club series and open series */}
                    {Object.keys(seriesByClub).length > 0 && openSeries.length > 0 && (
                      <div className="border-t border-border my-1" />
                    )}
                    
                    {/* Open Series Section */}
                    {openSeries.length > 0 && [
                      <div key="open-header" className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 rounded-sm">
                        {t('series.sections.openSeries')}
                      </div>,
                      ...openSeries.map((seriesItem) => (
                        <div
                          key={seriesItem.id}
                          onClick={() => {
                            updateSeriesSelection(seriesItem.id)
                            setSeriesDropdownOpen(false)
                          }}
                          className="flex items-center space-x-2 px-2 py-2 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground rounded-sm ml-4"
                          data-testid="series-option"
                          data-state={seriesItem.state || 'SERIES_STATE_UNSPECIFIED'}
                        >
                          <Icon icon={ICONS.tournament} width={16} height={16} className="text-success" />
                          <span className="flex-1">{seriesItem.title}</span>
                          {selectedSeriesId === seriesItem.id && (
                            <svg
                              className="ml-auto h-4 w-4 text-success"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20,6 9,17 4,12" />
                            </svg>
                          )}
                        </div>
                      ))
                    ]}
                  </div>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Selected Series Info */}
      {selectedSeries && (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center gap-3">
              <CardTitle className="flex items-center space-x-2">
                <Icon icon={ICONS.tournament} width={20} height={20} className="text-success" />
                <span>{selectedSeries.title}</span>
              </CardTitle>
              {isSeriesCompleted && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <Icon icon={ICONS.checkCircle} width={14} height={14} />
                  {t('series.status.completed')}
                </Badge>
              )}
            </div>
            {selectedSeries?.podium?.completedAt && (
              <CardDescription className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon icon={ICONS.calendar} width={14} height={14} />
                {t('series.seriesCompleted', {
                  date: new Date(selectedSeries.podium.completedAt).toLocaleDateString(),
                })}
              </CardDescription>
            )}
          </CardHeader>
        </Card>
      )}

      {/* Show message if user navigates to an incomplete knockout/cup series */}
      {selectedSeriesId && !isSeriesCompleted && 
       (seriesToDisplay?.format === 'SERIES_FORMAT_CUP' || 
        seriesToDisplay?.format === 'SERIES_FORMAT_GROUP_KNOCKOUT') && (
        <Alert>
          <AlertTitle>{t('leaderboard.seriesNotCompleted', 'Series not completed')}</AlertTitle>
          <AlertDescription>
            {t(
              'leaderboard.seriesNotCompletedDescription',
              'The podium will be available once the series is completed. Navigate to the series detail page to continue the tournament.'
            )}
          </AlertDescription>
        </Alert>
      )}

      {isSeriesCompleted && podiumToDisplay && (
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold text-foreground">{t('leaderboard.final_standings')}</h2>
          {loadingPodium ? (
            <div className="flex justify-center py-10">
              <LoadingSpinner />
            </div>
          ) : podiumError ? (
            <Alert variant="destructive">
              <AlertTitle>{t('leaderboard.podiumErrorTitle')}</AlertTitle>
              <AlertDescription>{podiumError}</AlertDescription>
            </Alert>
          ) : podiumView ? (
            <TournamentPodium
              champion={podiumView.champion}
              runnerUp={podiumView.runnerUp}
              thirdPlace={podiumView.thirdPlace}
              seriesTitle={seriesToDisplay?.title}
              completedAt={podiumToDisplay.completedAt}
            />
          ) : (
            <Alert>
              <AlertTitle>{t('leaderboard.podiumPendingTitle')}</AlertTitle>
              <AlertDescription>{t('leaderboard.podiumPendingDescription')}</AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {/* Leaderboard - Only show for non-completed series or if there are actual rankings */}
      {selectedSeriesId && !isSeriesCompleted && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="flex items-center space-x-2">
              <Icon icon={ICONS.podium} width={20} height={20} className="text-info" />
              <span>{leaderboardTitle}</span>
            </CardTitle>
            {leaderboard.length > 0 && !loadingLeaderboard && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                className="flex items-center gap-2"
              >
                <Icon icon={ICONS.export} width={16} height={16} />
                {t('leaderboard.exportCSV')}
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {loadingLeaderboard ? (
              <LoadingSpinner />
            ) : leaderboard.length === 0 ? (
              <div className="text-center py-8">
                <Icon icon={ICONS.tournament} width={48} height={48} className="mb-4 mx-auto text-muted-foreground" />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {t('leaderboard.empty')}
                </h3>
                <p className="text-muted-foreground">
                  {t('leaderboard.empty_help', 'No matches have been reported for this series yet.')}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-20">{t('leaderboard.rank')}</TableHead>
                      <TableHead>{t('leaderboard.player')}</TableHead>
                      <TableHead className="text-center">{t('leaderboard.rating')}</TableHead>
                      <TableHead className="text-center">{t('leaderboard.games')}</TableHead>
                      <TableHead className="text-center">{t('leaderboard.wins')}</TableHead>
                      <TableHead className="text-center">{t('leaderboard.losses')}</TableHead>
                      <TableHead className="text-center">{t('leaderboard.winrate')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leaderboard.map((row) => (
                      <TableRow key={row.playerId}>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            {getRankIcon(row.rank)}
                            <Badge variant={getRankBadge(row.rank)}>#{row.rank}</Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{row.displayName}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="font-mono font-semibold">
                            {Math.round(row.rating)}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">{row.games}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center space-x-1">
                            <Icon icon={ICONS.checkCircle} width={14} height={14} className="text-success" />
                            <span className="text-success font-medium">{row.wins}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center space-x-1">
                            <Icon icon={ICONS.closeCircle} width={14} height={14} className="text-destructive" />
                            <span className="text-destructive font-medium">{row.losses}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <span
                            className={`font-medium ${calculateWinRate(row.wins, row.games) >= 60
                              ? 'text-success'
                              : calculateWinRate(row.wins, row.games) >= 40
                                ? 'text-warning'
                                : 'text-destructive'
                              }`}
                          >
                            {calculateWinRate(row.wins, row.games)}%
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
